import { useState, useEffect } from "react";
import { FiFolder, FiFile, FiChevronRight, FiChevronDown, FiPlus, FiUpload, FiTrash2, FiEdit, FiDownload } from "react-icons/fi";
import Cookies from "js-cookie";
import { UploadPdfModal } from "../Users/Documents/UploadPdfModal";
import { SignPdfModal } from "../Users/Documents/SingPdfModal";
import getDocs from "../../services/Documents/getDocsData";
import signDoc from "../../services/Documents/signDoc";
import downloadDoc from "../../services/Documents/downloadDoc";

export default function FoldersView() {
  // Estructura de datos original
  const [items, setItems] = useState({});
  const [newItemName, setNewItemName] = useState("");
  const [expandedFolders, setExpandedFolders] = useState({});
  const [selectedItem, setSelectedItem] = useState(null);
  const [currentParent, setCurrentParent] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [isEditing, setIsEditing] = useState(false);

  // Nuevos estados para la funcionalidad de PDFs
  const [isDocumentDownloading, setIsDocumentDownloading] = useState({ documentId: '', isDownloading: false, isError: false });
  const [isSignModalOpen, setIsSignModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [certFile, setCertFile] = useState(null);
  const [keyFile, setKeyFile] = useState(null);
  const [pdf, setPdf] = useState(null);
  const [pdfName, setPdfName] = useState("");
  const [uploadStatus, setUploadStatus] = useState("");
  const [addStatus, setAddStatus] = useState("");
  const [userId, setUserId] = useState(null);
  const [documentsFromAPI, setDocumentsFromAPI] = useState([]);

  // Cargar datos del localStorage al iniciar
  useEffect(() => {
    const savedItems = localStorage.getItem("folderSystem");
    if (savedItems) {
      try {
        setItems(JSON.parse(savedItems));
      } catch (e) {
        console.error("Error parsing saved items:", e);
      }
    } else {
      // Crear algunas carpetas por defecto si no hay datos
      const defaultItems = {
        "folder-1": { id: "folder-1", name: "Casos Activos", parentId: null, type: "folder", files: [] },
        "folder-2": { id: "folder-2", name: "Contratos", parentId: null, type: "folder", files: [] },
        "folder-3": { id: "folder-3", name: "Documentos Importantes", parentId: null, type: "folder", files: [] }
      };
      setItems(defaultItems);
      localStorage.setItem("folderSystem", JSON.stringify(defaultItems));
    }

    // Obtener userId de cookies
    try {
      const userData = JSON.parse(Cookies.get("userData") || "{}");
      if (userData._id) {
        setUserId(userData._id);
      }
    } catch (error) {
      console.error("Error getting user data from cookies:", error);
    }
  }, []);

  // Cargar documentos de la API cuando tengamos userId
  useEffect(() => {
    if (userId) {
      const fetchDocuments = async () => {
        try {
          const docs = await getDocs(userId);
          setDocumentsFromAPI(docs);
        } catch (error) {
          console.error("Error fetching documents:", error);
        }
      };
      fetchDocuments();
    }
  }, [userId]);

  // Guardar en localStorage cuando los items cambien
  useEffect(() => {
    if (Object.keys(items).length > 0) {
      localStorage.setItem("folderSystem", JSON.stringify(items));
    }
  }, [items]);

  // Calcular breadcrumbs cuando cambia el padre actual
  useEffect(() => {
    if (currentParent === null) {
      setBreadcrumbs([{ id: null, name: "Inicio" }]);
      return;
    }

    const newBreadcrumbs = [];
    let parent = currentParent;

    // Añadir el folder actual y sus padres
    while (parent !== null) {
      const folder = items[parent];
      if (!folder) break;
      newBreadcrumbs.unshift({ id: folder.id, name: folder.name });
      parent = folder.parentId;
    }

    // Añadir "Inicio" al principio
    newBreadcrumbs.unshift({ id: null, name: "Inicio" });
    setBreadcrumbs(newBreadcrumbs);
  }, [currentParent, items]);

  const createNewItem = (type) => {
    if (newItemName.trim() === "") return;

    const newId = `${type}-${Date.now()}`;
    const newItem = {
      id: newId,
      name: newItemName,
      parentId: currentParent,
      type: type,
      files: type === "folder" ? [] : undefined
    };

    setItems(prevItems => {
      const updatedItems = { ...prevItems, [newId]: newItem };

      // Si tiene padre, actualizar el array de files del padre
      if (currentParent !== null && type === "folder") {
        const parent = { ...prevItems[currentParent] };
        parent.files = [...parent.files, newId];
        updatedItems[currentParent] = parent;
      }

      return updatedItems;
    });

    setNewItemName("");
  };

  // Método original modificado para usar el modal
  const uploadDocument = () => {
    setIsUploadModalOpen(true);
    setPdf(null);
    setPdfName("");
    setAddStatus("");
  };

  // Método para procesar el documento subido desde el modal
  const processUploadedDocument = (file, fileName) => {
    if (!file) return;

    const newId = `file-${Date.now()}`;
    const newFile = {
      id: newId,
      name: fileName || file.name,
      parentId: currentParent,
      type: "file",
      size: file.size,
      lastModified: file.lastModified,
      isFromAPI: true,
      apiReference: file,
      isSigned: false
    };

    setItems(prevItems => {
      const updatedItems = { ...prevItems, [newId]: newFile };

      // Si tiene padre, actualizar el array de files del padre
      if (currentParent !== null) {
        const parent = { ...prevItems[currentParent] };
        parent.files = [...parent.files, newId];
        updatedItems[currentParent] = parent;
      }

      return updatedItems;
    });

    setIsUploadModalOpen(false);
  };

  const toggleFolder = (folderId) => {
    setExpandedFolders(prev => ({
      ...prev,
      [folderId]: !prev[folderId]
    }));
  };

  const navigateToFolder = (folderId) => {
    setCurrentParent(folderId);
    setSelectedItem(null);
  };

  const deleteItem = (itemId) => {
    if (!itemId) return;

    setItems(prevItems => {
      const updatedItems = { ...prevItems };
      const itemToDelete = updatedItems[itemId];

      // Si es una carpeta, eliminar recursivamente todos los elementos dentro
      if (itemToDelete.type === "folder") {
        // Función recursiva para eliminar carpeta y su contenido
        const deleteFolder = (folderId) => {
          const folder = updatedItems[folderId];
          if (!folder) return;

          // Eliminar archivos y subcarpetas
          if (folder.files && folder.files.length > 0) {
            folder.files.forEach(childId => {
              const child = updatedItems[childId];
              if (child.type === "folder") {
                deleteFolder(childId);
              }
              delete updatedItems[childId];
            });
          }

          // Eliminar esta carpeta
          delete updatedItems[folderId];
        };

        deleteFolder(itemId);
      } else {
        // Es un archivo, simplemente eliminar
        delete updatedItems[itemId];
      }

      // Actualizar la lista de archivos del padre
      if (itemToDelete.parentId !== null) {
        const parent = updatedItems[itemToDelete.parentId];
        if (parent) {
          parent.files = parent.files.filter(id => id !== itemId);
        }
      }

      return updatedItems;
    });

    setSelectedItem(null);
  };

  const startEditing = (item) => {
    setSelectedItem(item);
    setNewItemName(item.name);
    setIsEditing(true);
  };

  const saveEdit = () => {
    if (newItemName.trim() === "" || !selectedItem) return;

    setItems(prevItems => {
      const updatedItems = { ...prevItems };
      updatedItems[selectedItem.id] = {
        ...updatedItems[selectedItem.id],
        name: newItemName
      };
      return updatedItems;
    });

    setNewItemName("");
    setIsEditing(false);
    setSelectedItem(null);
  };

  const cancelEdit = () => {
    setNewItemName("");
    setIsEditing(false);
    setSelectedItem(null);
  };

  // Nuevas funciones para integración de firma y descarga
  const handleSignDocument = (document) => {
    setSelectedItem(document);
    setIsSignModalOpen(true);
    setCertFile(null);
    setKeyFile(null);
    setUploadStatus("");
  };

  const closeSignModal = () => {
    setIsSignModalOpen(false);
    setSelectedItem(null);
  };

  const handleDownloadDocument = async (e, document) => {
    e.stopPropagation();
    
    if (!document.isFromAPI) {
      console.error("Cannot download local files");
      return;
    }
    
    setIsDocumentDownloading({documentId: document.id, isDownloading: true, isError: false});
    const isError = await downloadDoc(userId, document.apiReference.metadataId.name);
    setIsDocumentDownloading({documentId: document.id, isDownloading: false, isError: isError});
    
    // Actualizar el estado del documento si se descargó con éxito
    if (!isError) {
      setItems(prevItems => {
        const updatedItems = { ...prevItems };
        updatedItems[document.id] = {
          ...updatedItems[document.id],
          wasDownloaded: true
        };
        return updatedItems;
      });
    }
  };

  // Filtrar los elementos para mostrar solo los del nivel actual
  const getCurrentLevelItems = () => {
    return Object.values(items).filter(item => item.parentId === currentParent);
  };

  // Renderizar el árbol de carpetas para la navegación lateral
  const renderFolderTree = (parentId = null, level = 0) => {
    const folders = Object.values(items).filter(
      item => item.type === "folder" && item.parentId === parentId
    );

    return folders.map(folder => (
      <div key={folder.id} className="ml-4">
        <div
          className={`flex items-center py-1 cursor-pointer hover:bg-gray-100 ${currentParent === folder.id ? "bg-blue-50" : ""
            }`}
          onClick={() => navigateToFolder(folder.id)}
        >
          <span
            className="mr-1 text-gray-500 cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              toggleFolder(folder.id);
            }}
          >
            {expandedFolders[folder.id] ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}
          </span>
          <FiFolder className="text-blue-500 mr-1" size={16} />
          <span className="text-sm truncate">{folder.name}</span>
        </div>

        {expandedFolders[folder.id] && renderFolderTree(folder.id, level + 1)}
      </div>
    ));
  };

  return (
    <div className="flex h-full">
      {/* Navegación lateral de carpetas */}
      <div className="w-64 bg-white border-r p-4 h-full overflow-y-auto">
        <h2 className="text-lg font-medium mb-4">Explorador</h2>

        <div
          className={`flex items-center py-1 cursor-pointer hover:bg-gray-100 ${currentParent === null ? "bg-blue-50" : ""
            }`}
          onClick={() => navigateToFolder(null)}
        >
          <FiFolder className="text-blue-500 mr-2" />
          <span className="font-medium">Carpetas principales</span>
        </div>

        {renderFolderTree()}
      </div>

      {/* Contenido principal */}
      <div className="flex-1 p-6 bg-gray-50 h-full overflow-y-auto">
        {/* Breadcrumbs */}
        <div className="flex items-center mb-4 text-sm text-gray-600">
          {breadcrumbs.map((crumb, index) => (
            <div key={crumb.id || "home"} className="flex items-center">
              <span
                className={index < breadcrumbs.length - 1 ? "cursor-pointer hover:text-blue-600" : "font-medium"}
                onClick={() => index < breadcrumbs.length - 1 ? navigateToFolder(crumb.id) : null}
              >
                {crumb.name}
              </span>
              {index < breadcrumbs.length - 1 && <span className="mx-2">/</span>}
            </div>
          ))}
        </div>

        {/* Encabezado y acciones */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-semibold text-navy-900">
            {currentParent === null ? "Carpetas" : items[currentParent]?.name}
          </h1>

          <div className="flex space-x-2">
            {isEditing ? (
              <>
                <input
                  type="text"
                  value={newItemName}
                  onChange={(e) => setNewItemName(e.target.value)}
                  className="px-3 py-2 border rounded"
                  placeholder="Nuevo nombre"
                />
                <button
                  onClick={saveEdit}
                  className="px-4 py-2 bg-[#1A365D] text-white rounded hover:bg-indigo-800"
                >
                  Guardar
                </button>
                <button
                  onClick={cancelEdit}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                >
                  Cancelar
                </button>
              </>
            ) : (
              <>
                <input
                  type="text"
                  value={newItemName}
                  onChange={(e) => setNewItemName(e.target.value)}
                  placeholder="Nombre"
                  className="px-3 py-2 border rounded"
                />
                <button
                  onClick={() => createNewItem("folder")}
                  className="px-4 py-2 bg-[#1A365D] text-white rounded hover:bg-opacity-90 flex items-center"
                >
                  <FiPlus className="mr-1" /> Carpeta
                </button>
                <button
                  onClick={uploadDocument}
                  className="px-4 py-2 bg-[#B8860B] text-white rounded hover:bg-opacity-90 cursor-pointer flex items-center"
                >
                  <FiUpload className="mr-1" /> Documento
                </button>
              </>
            )}
          </div>
        </div>

        {/* Lista de elementos */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {getCurrentLevelItems().map((item) => (
            <div
              key={item.id}
              className={`bg-white p-4 rounded-lg shadow hover:shadow-md transition-shadow ${selectedItem?.id === item.id ? "ring-2 ring-blue-500" : ""
                }`}
              onClick={() => setSelectedItem(item)}
            >
              <div className="flex items-center">
                {item.type === "folder" ? (
                  <FiFolder className="h-8 w-8 text-blue-500 mr-3" />
                ) : (
                  <FiFile className="h-8 w-8 text-gray-500 mr-3" />
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium truncate">{item.name}</h3>
                  {item.type === "file" && (
                    <p className="text-sm text-gray-500">
                      {(item.size / 1024).toFixed(1)} KB
                      {item.isSigned && (
                        <span className="ml-2 text-green-600 font-medium">Firmado</span>
                      )}
                    </p>
                  )}
                </div>
              </div>
              <div className="mt-4 flex justify-end space-x-2">
                {item.type === "folder" && (
                  <button
                    className="px-3 py-1 text-xs bg-gray-100 rounded hover:bg-gray-200"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigateToFolder(item.id);
                    }}
                  >
                    Abrir
                  </button>
                )}
                {item.type === "file" && (
                  <>
                    {item.isFromAPI && (
                      <button
                        className={`px-3 py-1 text-xs bg-blue-50 text-indigo-900 rounded hover:bg-blue-100 ${
                          isDocumentDownloading.documentId === item.id && isDocumentDownloading.isDownloading ? 'opacity-50' : ''
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownloadDocument(e, item);
                        }}
                        disabled={isDocumentDownloading.isDownloading}
                      >
                        <FiDownload size={12} />
                      </button>
                    )}
                    {item.isFromAPI && !item.isSigned && (
                      <button
                        className="px-3 py-1 text-xs bg-green-50 text-green-900 rounded hover:bg-green-100"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSignDocument(item);
                        }}
                      >
                        Firmar
                      </button>
                    )}
                  </>
                )}
                <button
                  className="px-3 py-1 text-xs bg-blue-50 text-indigo-900 rounded hover:bg-blue-100"
                  onClick={(e) => {
                    e.stopPropagation();
                    startEditing(item);
                  }}
                >
                  <FiEdit size={12} />
                </button>
                <button
                  className="px-3 py-1 text-xs bg-red-50 text-yellow-600 rounded hover:bg-red-100"
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteItem(item.id);
                  }}
                >
                  <FiTrash2 size={12} />
                </button>
              </div>
            </div>
          ))}

          {getCurrentLevelItems().length === 0 && (
            <div className="col-span-3 text-center py-12 text-gray-500">
              Esta carpeta está vacía. Crea una carpeta o sube un documento.
            </div>
          )}
        </div>
      </div>

      {/* Modal para subir documentos */}
      {isUploadModalOpen && (
        <UploadPdfModal
          addStatus={addStatus}
          closeAddModal={() => setIsUploadModalOpen(false)}
          pdf={pdf}
          pdfName={pdfName}
          setAddStatus={setAddStatus}
          setDocuments={(docs) => {
            setDocumentsFromAPI(docs);
            // Procesar el documento subido para la vista de carpetas
            if (pdf) {
              processUploadedDocument(pdf, pdfName);
            }
          }}
          setPdf={setPdf}
          setPdfName={setPdfName}
          userId={userId}
        />
      )}

      {/* Modal para firmar documentos */}
      {isSignModalOpen && selectedItem && (
        <SignPdfModal
          certFile={certFile}
          closeModal={closeSignModal}
          keyFile={keyFile}
          selectedDocument={selectedItem.apiReference}
          setCertFile={setCertFile}
          setDocuments={(docs) => {
            setDocumentsFromAPI(docs);
            // Actualizar el estado del documento para mostrar que está firmado
            setItems(prevItems => {
              const updatedItems = { ...prevItems };
              if (selectedItem && updatedItems[selectedItem.id]) {
                updatedItems[selectedItem.id] = {
                  ...updatedItems[selectedItem.id],
                  isSigned: true
                };
              }
              return updatedItems;
            });
          }}
          setKeyFile={setKeyFile}
          setUploadStatus={setUploadStatus}
          signDoc={signDoc}
          uploadStatus={uploadStatus}
          userId={userId}
        />
      )}
    </div>
  );
}